import React, { useEffect, useState } from 'react';
import SortableItemList from '../../../../components/SortableItemList';
import { activateAuthLayout } from '../../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Select from 'react-select';
import { ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button } from 'reactstrap';

const DATA = gql`
    query getData {
        Icons {
            IconId
            Handle
            Title
        }
        ProductCategories {
            CatId
            Name
            Icon
            SortOrder
            Products {
                ProductId
            }
        }
    }
`;

const CREATE_CATEGORY = gql`
    mutation createProductCategory($ProductCategoryInput: ProductCategoryInput) {
        createProductCategory(input: $ProductCategoryInput) {
            CatId
            Name
            Icon
            SortOrder
        }
    }
`;

const UPDATE_CATEGORY = gql`
    mutation updateProductCategory($ProductCategoryInput: ProductCategoryInput) {
        updateProductCategory(input: $ProductCategoryInput) {
            CatId
            Name
            Icon
            SortOrder
        }
    }
`;

const DELETE_CATEGORY = gql`
    mutation deleteProductCategory($CatId: Int!) {
        deleteProductCategory(CatId: $CatId) {
            CatId
        }
    }
`;

const CategoryForm = ({ item, iconOptions, onSave, onClose }) => {
    const [name, setName] = useState(item?.Name ?? '');
    const [icon, setIcon] = useState(item?.Icon ?? null);

    const save = (e) => {
        e?.preventDefault();
        Promise.resolve(onSave({ name, icon })).then(onClose);
    };

    return (
        <>
            <ModalBody>
                <Form onSubmit={save}>
                    <FormGroup>
                        <Label>Category Name <small className="text-danger">*</small></Label>
                        <Input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            onKeyPress={e => e.key === 'Enter' && name.trim() && save()}
                            autoFocus
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Icon</Label>
                        <Select
                            options={iconOptions}
                            value={iconOptions.find(o => o.value === icon) ?? null}
                            onChange={selected => setIcon(selected?.value ?? null)}
                            isClearable
                            placeholder="Search icons..."
                            formatOptionLabel={option => (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <i className={`fas ${option.value}`} style={{ width: '16px', textAlign: 'center' }} />
                                    <span>{option.label}</span>
                                </div>
                            )}
                        />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={save} disabled={!name.trim()}>Save</Button>
            </ModalFooter>
        </>
    );
};

const CategoryList = ({ activateAuthLayout }) => {
    useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

    const { data, loading, refetch } = useQuery(DATA);
    const [createProductCategory] = useMutation(CREATE_CATEGORY);
    const [updateProductCategory] = useMutation(UPDATE_CATEGORY);
    const [deleteProductCategory] = useMutation(DELETE_CATEGORY);

    const iconOptions = (data?.Icons ?? []).map(icon => ({ value: icon.Handle, label: icon.Title }));

    return (
        <SortableItemList
            title="Categories"
            loading={loading}
            items={data?.ProductCategories ?? []}
            getKey={c => c.CatId}
            getLabel={c => c.Name}
            renderLabel={c => (
                <span>
                    {c.Icon && <i className={`fas ${c.Icon} mr-2`} />}
                    {c.Name}
                </span>
            )}
            droppableId="product-categories"
            onReorder={reordered => reordered.forEach((c, i) =>
                updateProductCategory({ variables: { ProductCategoryInput: { CatId: c.CatId, SortOrder: i + 1 } } })
            )}
            onDelete={c =>
                deleteProductCategory({ variables: { CatId: c.CatId } }).then(() => refetch())
            }
            canDelete={c => c.Products.length === 0}
            deleteBlockedMessage="This category is assigned to one or more products and cannot be deleted."
            deleteWarning={c => `Delete category "${c.Name}"?`}
            createLabel="Create New Category"
            editLabel="Edit"
            createTitle="Create Category"
            editTitle="Edit Category"
            emptyMessage="No product categories exist."
            renderForm={(item, close) => (
                <CategoryForm
                    item={item}
                    iconOptions={iconOptions}
                    onClose={close}
                    onSave={({ name, icon }) => item
                        ? updateProductCategory({ variables: { ProductCategoryInput: { CatId: item.CatId, Name: name, Icon: icon } } }).then(() => refetch())
                        : createProductCategory({ variables: { ProductCategoryInput: { Name: name, Icon: icon } } }).then(() => refetch())
                    }
                />
            )}
        />
    );
};

export default connect(null, { activateAuthLayout })(CategoryList);
